import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message, Spin, Input } from "antd";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import "./managecerts.css";
import { generateCertificatePdf } from "../../util/generateCertificatePdf";

interface ECertRequest {
  docId: string;
  fullName: string;
  position: string;
  organization: string;
  email: string;
  phone?: string;
  status?: string;
  feedback?: string;
}

const ManageECertificates: React.FC = () => {
  const [requests, setRequests] = useState<ECertRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [previewData, setPreviewData] = useState<ECertRequest | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);

  // === TESTIMONIAL STATES ===
  const [isTestimonialVisible, setIsTestimonialVisible] =
    useState<boolean>(false);
  const [testimonialData, setTestimonialData] = useState<ECertRequest | null>(
    null,
  );
  const [testimonialPreviewUrl, setTestimonialPreviewUrl] =
    useState<string>("");

  // =============================
  // EXPORT TO EXCEL
  // =============================
  const exportToExcel = () => {
    if (requests.length === 0) {
      message.warning("No records to export.");
      return;
    }

    const formatted = requests.map((item) => ({
      "Full Name": item.fullName,
      Email: item.email,
      Organization: item.organization,
      Position: item.position,
      Phone: item.phone || "",
      Status: item.status || "",
      Feedback: item.feedback || "",
    }));

    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "E-Cert Requests");
      XLSX.writeFile(workbook, "ECertificateRequests.xlsx");
    });
  };

  const [searchTerm, setSearchTerm] = useState("");

  // =============================
  // LOAD REQUESTS
  // =============================
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "ecertificate_requests"));
      const list: ECertRequest[] = snap.docs.map((d) => {
        const data = d.data();

        return {
          docId: d.id,
          fullName: data.fullName ?? "",
          position: data.position ?? "",
          organization: data.organization ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          status: data.status ?? "",
          feedback: data.feedback ?? "",
        };
      });

      setRequests(list);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch certificate requests.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (!searchTerm) return true;

    const text =
      `${r.fullName} ${r.email} ${r.organization} ${r.position}`.toLowerCase();

    return text.includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  // ====================================
  // OPEN CERTIFICATE PREVIEW MODAL
  // ====================================
  const openPreview = (record: ECertRequest) => {
    setPreviewData(record);
    setIsPreviewVisible(true);
  };

  // ====================================
  // OPEN TESTIMONIAL & GENERATE CANVAS
  // ====================================
  const openTestimonial = async (record: ECertRequest) => {
    setTestimonialData(record);
    setIsTestimonialVisible(true);

    const canvas = await generateTestimonialImage(record);
    const url = canvas.toDataURL("image/png");
    setTestimonialPreviewUrl(url);
  };

  // ====================================
  // SEND CERTIFICATE EMAIL
  // ====================================
  const handleSendCertificate = async () => {
    if (!previewData) return;

    setSending(true);

    try {
      const pdfDoc = await generateCertificatePdf({
        name: previewData.fullName,
        position: previewData.position,
        organization: previewData.organization,
      });

      const pdfBase64 = pdfDoc.output("datauristring").split(",")[1];

      await fetch(
        "https://us-central1-icaconference-438ba.cloudfunctions.net/sendCertificateEmail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            personalizations: [
              {
                to: [{ email: previewData.email }],
                dynamic_template_data: {
                  fullName: previewData.fullName,
                },
              },
            ],
            attachments: [
              {
                content: pdfBase64,
                filename: `${previewData.fullName}-Certificate.pdf`,
                type: "application/pdf",
                disposition: "attachment",
              },
            ],
          }),
        },
      );

      await updateDoc(doc(db, "ecertificate_requests", previewData.docId), {
        status: "sent",
      });

      message.success("Certificate sent successfully!");
      setIsPreviewVisible(false);
      fetchRequests();
    } catch (err) {
      console.error(err);
      message.error("Failed to send certificate.");
    } finally {
      setSending(false);
    }
  };

  // ====================================
  // TABLE COLUMNS
  // ====================================
  const columns = [
    { title: "Full Name", dataIndex: "fullName", key: "fullName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Organization", dataIndex: "organization", key: "organization" },
    { title: "Position", dataIndex: "position", key: "position" },
    { title: "Mobile", dataIndex: "phone", key: "phone" },
    { title: "Feedback", dataIndex: "feedback", key: "feedback", width: 320 },
    {
      title: "Action",
      key: "action",
      render: (_: unknown, record: ECertRequest) => (
        <>
          <Button
            type="primary"
            onClick={() => openPreview(record)}
            style={{ marginRight: 8 }}
          >
            Preview & Send
          </Button>

          <Button onClick={() => openTestimonial(record)}>Feedback</Button>
        </>
      ),
    },
  ];

  const generateTestimonialImage = async (
    data: ECertRequest,
  ): Promise<HTMLCanvasElement> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

      canvas.width = 1080;
      canvas.height = 1080;

      /* =============================
       1. DARK GRADIENT BACKGROUND
    ============================= */
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#002423");
      gradient.addColorStop(1, "#003b38");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      /* =============================
       2. TEXT MEASUREMENT
    ============================= */
      const quote = `“${(data.feedback || "").trim()}”`;

      ctx.font = "36px Inter, sans-serif";
      ctx.textAlign = "center";

      const maxWidth = 720;
      const lineHeight = 48;
      const lines = wrapLines(ctx, quote, maxWidth);
      const textHeight = lines.length * lineHeight;

      /* Layout metrics */
      const topPadding = 100;
      const bottomPadding = 100;
      const authorAreaHeight = 150;
      const quoteMarginBottom = 40;

      /* =============================
       3. PANEL HEIGHT CALCULATION
    ============================= */
      const panelH =
        topPadding +
        textHeight +
        quoteMarginBottom +
        authorAreaHeight +
        bottomPadding;

      const panelW = 880;
      const panelX = (canvas.width - panelW) / 2;

      /* =============================
       4. CENTER PANEL VERTICALLY
    ============================= */
      const panelY = (canvas.height - panelH) / 2;

      /* =============================
       5. GLASS PANEL DRAWING
    ============================= */
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      drawRoundedRect(ctx, panelX, panelY, panelW, panelH, 40);
      ctx.fill();

      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.stroke();

      /* =============================
       6. QUOTE TEXT (CENTERED)
    ============================= */
      ctx.fillStyle = "#ffffff";
      ctx.font = "36px Inter, sans-serif";

      let textY = panelY + topPadding;

      lines.forEach((line) => {
        ctx.fillText(line, canvas.width / 2, textY);
        textY += lineHeight;
      });

      textY += quoteMarginBottom;

      /* =============================
       7. AUTHOR INFO (BOTTOM LEFT)
    ============================= */
      ctx.textAlign = "left";

      const leftX = panelX + 50;
      let authorY = textY;

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 32px Inter, sans-serif";
      ctx.fillText(data.fullName, leftX, authorY);

      authorY += 44;

      ctx.font = "26px Inter, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillText(data.position || "", leftX, authorY);

      authorY += 38;

      ctx.fillText(data.organization || "", leftX, authorY);

      resolve(canvas);
    });
  };

  /* ===========================================
   LINE WRAP
=========================================== */
  function wrapLines(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let line = "";

    for (const w of words) {
      const testLine = line + w + " ";
      if (ctx.measureText(testLine).width > maxWidth) {
        if (line.trim()) lines.push(line.trim());
        line = w + " ";
      } else {
        line = testLine;
      }
    }
    if (line.trim()) lines.push(line.trim());
    return lines;
  }

  /* ===========================================
   ROUNDED RECT
=========================================== */
  function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  //   const generateTestimonialImage = async (
  //     data: ECertRequest
  //   ): Promise<HTMLCanvasElement> => {
  //     return new Promise((resolve) => {
  //       const canvas = document.createElement("canvas");
  //       const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

  //       canvas.width = 1080;
  //       canvas.height = 1080;

  //       /* =============================
  //        1. DARK GRADIENT BACKGROUND
  //     ============================= */
  //       const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  //       gradient.addColorStop(0, "#002423");
  //       gradient.addColorStop(1, "#003b38");
  //       ctx.fillStyle = gradient;
  //       ctx.fillRect(0, 0, canvas.width, canvas.height);

  //       /* =============================
  //        2. TEXT MEASUREMENT
  //     ============================= */
  //       const quote = `“${(data.feedback || "").trim()}”`;

  //       ctx.font = "36px Inter, sans-serif";
  //       ctx.textAlign = "center";

  //       const maxWidth = 720;
  //       const lineHeight = 48;
  //       const lines = wrapLines(ctx, quote, maxWidth);
  //       const textHeight = lines.length * lineHeight;

  //       const topPadding = 90;
  //       const bottomPadding = 30;
  //       const authorAreaHeight = 150;
  //       const quoteMarginBottom = 40;

  //       /* =============================
  //        3. PANEL HEIGHT (NO OVERLAP EVER)
  //     ============================= */
  //       const panelH =
  //         topPadding +
  //         textHeight +
  //         quoteMarginBottom +
  //         authorAreaHeight +
  //         bottomPadding;

  //       const panelW = 880;
  //       const panelX = (canvas.width - panelW) / 2;

  //       // With no logos → choose a visually balanced top offset
  //       const panelY = 160;

  //       /* =============================
  //        4. GLASS PANEL
  //     ============================= */
  //       ctx.fillStyle = "rgba(255,255,255,0.1)";
  //       drawRoundedRect(ctx, panelX, panelY, panelW, panelH, 40);
  //       ctx.fill();

  //       ctx.lineWidth = 2;
  //       ctx.strokeStyle = "rgba(255,255,255,0.18)";
  //       ctx.stroke();

  //       /* =============================
  //        5. QUOTE TEXT — PERFECT CENTER
  //     ============================= */
  //       ctx.fillStyle = "#ffffff";
  //       ctx.font = "36px Inter, sans-serif";

  //       let textY = panelY + topPadding;

  //       lines.forEach((line) => {
  //         ctx.fillText(line, canvas.width / 2, textY);
  //         textY += lineHeight;
  //       });

  //       textY += quoteMarginBottom;

  //       /* =============================
  //        6. AUTHOR INFO (BOTTOM LEFT)
  //     ============================= */
  //       ctx.textAlign = "left";

  //       const leftX = panelX + 50;
  //       let authorY = textY + 10;

  //       ctx.fillStyle = "#ffffff";
  //       ctx.font = "bold 32px Inter, sans-serif";
  //       ctx.fillText(data.fullName, leftX, authorY);

  //       authorY += 44;

  //       ctx.font = "26px Inter, sans-serif";
  //       ctx.fillStyle = "rgba(255,255,255,0.85)";
  //       ctx.fillText(data.position || "", leftX, authorY);

  //       authorY += 38;

  //       ctx.fillText(data.organization || "", leftX, authorY);

  //       resolve(canvas);
  //     });
  //   };

  //   /* ===========================================
  //    LINE WRAP
  // =========================================== */
  //   function wrapLines(
  //     ctx: CanvasRenderingContext2D,
  //     text: string,
  //     maxWidth: number
  //   ): string[] {
  //     const words = text.split(" ");
  //     const lines: string[] = [];
  //     let line = "";

  //     for (const w of words) {
  //       const testLine = line + w + " ";
  //       if (ctx.measureText(testLine).width > maxWidth) {
  //         if (line.trim()) lines.push(line.trim());
  //         line = w + " ";
  //       } else {
  //         line = testLine;
  //       }
  //     }
  //     if (line.trim()) lines.push(line.trim());
  //     return lines;
  //   }

  //   /* ===========================================
  //    ROUNDED RECT
  // =========================================== */
  //   function drawRoundedRect(
  //     ctx: CanvasRenderingContext2D,
  //     x: number,
  //     y: number,
  //     w: number,
  //     h: number,
  //     r: number
  //   ) {
  //     ctx.beginPath();
  //     ctx.moveTo(x + r, y);
  //     ctx.arcTo(x + w, y, x + w, y + h, r);
  //     ctx.arcTo(x + w, y + h, x, y + h, r);
  //     ctx.arcTo(x, y + h, x, y, r);
  //     ctx.arcTo(x, y, x + w, y, r);
  //     ctx.closePath();
  //   }

  // ====================================
  // RENDER
  // ====================================
  return (
    <div className="ecert-dashboard">
      <h1 className="ecert-title">E-Certificate Requests</h1>

      {loading ? (
        <Spin size="large" />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Input
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                height: 44,
                borderRadius: 14,
                width: 360,
              }}
            />

            <Button type="default" onClick={exportToExcel}>
              Export to Excel
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={requests}
            rowKey={(record) => record.docId}
            pagination={{ pageSize: 10 }}
          />
        </>
      )}

      {/* ====================================
          CERTIFICATE PREVIEW MODAL
      ==================================== */}
      <Modal
        title="Certificate Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setIsPreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            disabled={!previewData}
            onClick={async () => {
              if (!previewData) return;
              const pdfDoc = await generateCertificatePdf({
                name: previewData.fullName,
                position: previewData.position,
                organization: previewData.organization,
              });
              pdfDoc.save(`${previewData.fullName}-Certificate.pdf`);
            }}
          >
            Download PDF
          </Button>,
          <Button
            key="send"
            type="primary"
            style={{ background: "#0A4A45" }}
            onClick={handleSendCertificate}
            disabled={sending}
          >
            {sending ? (
              <>
                <Spin size="small" style={{ marginRight: 8 }} />
                Sending...
              </>
            ) : (
              "Send Certificate"
            )}
          </Button>,
        ]}
      >
        {previewData && (
          <div className="certificate-preview-box">
            <div className="certificate-preview-wrapper">
              <img
                src="/images/certificate_bg.png"
                className="certificate-preview-image"
              />
              <div className="cert-overlay">
                <div className="cert-name">{previewData.fullName}</div>
                <div className="cert-position">{previewData.position}</div>
                <div className="cert-organization">
                  {previewData.organization}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ====================================
          TESTIMONIAL PREVIEW MODAL
      ==================================== */}
      <Modal
        title="Feedback Preview"
        open={isTestimonialVisible}
        onCancel={() => setIsTestimonialVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setIsTestimonialVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => {
              if (!testimonialPreviewUrl || !testimonialData) return;

              const link = document.createElement("a");
              link.download = `${testimonialData.fullName}-testimonial.png`;
              link.href = testimonialPreviewUrl;
              link.click();
            }}
          >
            Download PNG
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center" }}>
          {testimonialPreviewUrl ? (
            <img
              src={testimonialPreviewUrl}
              alt="Generated Feedback"
              style={{ width: "85%", borderRadius: 12 }}
            />
          ) : (
            <Spin />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ManageECertificates;
